import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Sparkles, MapPin, Phone, MessageCircle, Instagram, Menu, X, 
  ChevronRight, Star, Award, CheckCircle, Users, Scissors, 
  Trash2, LogOut, RefreshCw, Send, Lock, Loader2, Play, AlertCircle
} from "lucide-react";
import axios from "axios";
import { HOME } from "@/constants/testIds";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://sam-makeover.preview.emergentagent.com";
const API = `${BACKEND_URL}/api`;

export default function App() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Lead submission form state
  const [enrollForm, setEnrollForm] = useState({
    name: "",
    email: "",
    phone: "",
    course: "Professional Makeup",
    message: ""
  });
  const [formSubmitting, setFormFormSubmitting] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);

  // Admin section state
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Gallery / media storage state
  const [galleryUploads, setGalleryUploads] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("Bridal Makeup");
  const [uploading, setUploading] = useState(false);

  // AI Chat Assistant state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSessionId, setChatSessionId] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreamingText, setChatStreamingText] = useState("");
  const chatEndRef = useRef(null);

  // Initialize Chat Session ID
  useEffect(() => {
    let session = localStorage.getItem("sam_beauty_session");
    if (!session) {
      session = "sess_" + Math.random().toString(36).substring(2, 15);
      localStorage.setItem("sam_beauty_session", session);
    }
    setChatSessionId(session);
    loadChatHistory(session);
  }, []);

  useEffect(() => {
    if (chatOpen) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, chatStreamingText, chatOpen]);

  // Load chat history on init
  const loadChatHistory = async (session) => {
    try {
      const res = await axios.get(`${API}/chat/history/${session}`);
      if (res.data) {
        setChatMessages(res.data);
      }
    } catch (e) {
      console.error("Error loading chat history:", e);
    }
  };

  // Load gallery uploads on init
  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      if (res.data) setGalleryUploads(res.data);
    } catch (e) {
      console.error("Error loading gallery:", e);
    }
  };

  // Upload gallery image (admin)
  const handleGalleryUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast({ title: "No file selected", description: "Please choose an image to upload.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadTitle);
      formData.append("category", uploadCategory);
      formData.append("password", adminPassword);
      await axios.post(`${API}/gallery/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast({ title: "Image Uploaded", description: "Your work photo is now live in the gallery." });
      setUploadFile(null);
      setUploadTitle("");
      document.getElementById("gallery-file-input") && (document.getElementById("gallery-file-input").value = "");
      fetchGallery();
    } catch (error) {
      toast({ title: "Upload Failed", description: error.response?.data?.detail || "Could not upload image.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  // Delete gallery image (admin)
  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}?password=${adminPassword}`);
      toast({ title: "Image Removed", description: "The image has been removed from the gallery." });
      fetchGallery();
    } catch (error) {
      toast({ title: "Delete Failed", description: "Could not remove image.", variant: "destructive" });
    }
  };

  // Submit contact form
  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    if (!enrollForm.name || !enrollForm.email || !enrollForm.phone) {
      toast({
        title: "Incomplete Form",
        description: "Please fill in all the required fields.",
        variant: "destructive"
      });
      return;
    }
    
    setFormFormSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, enrollForm);
      if (res.status === 201) {
        toast({
          title: "Enrollment Inquiry Sent!",
          description: `Thank you, ${enrollForm.name}. Our admissions officer will contact you shortly!`,
          variant: "default"
        });
        setEnrollForm({
          name: "",
          email: "",
          phone: "",
          course: "Professional Makeup",
          message: ""
        });
        setShowEnrollModal(false);
      }
    } catch (error) {
      console.error("Enrollment error:", error);
      toast({
        title: "Submission Failed",
        description: "There was an issue sending your inquiry. Please try again or call directly.",
        variant: "destructive"
      });
    } finally {
      setFormFormSubmitting(false);
    }
  };

  // Admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/leads?password=${adminPassword}`);
      if (res.data) {
        setLeads(res.data);
        setIsAdminLoggedIn(true);
        toast({
          title: "Welcome Admin",
          description: "Leads successfully synchronized.",
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Access Denied",
        description: "Incorrect admin password. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleAdminLogout = () => {
    setIsAdminLoggedIn(false);
    setAdminPassword("");
    setLeads([]);
    toast({
      title: "Logged Out",
      description: "Admin session closed.",
      variant: "default"
    });
  };

  // Fetch leads when logged in
  const refreshLeads = async () => {
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/leads?password=${adminPassword}`);
      setLeads(res.data);
    } catch (error) {
      console.error("Refresh error:", error);
    } finally {
      setLoadingLeads(false);
    }
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status?password=${adminPassword}`, { status: newStatus });
      toast({
        title: "Status Updated",
        description: `Lead status is now: ${newStatus}`,
        variant: "default"
      });
      refreshLeads();
    } catch (error) {
      toast({
        title: "Update Failed",
        description: "Could not update lead status.",
        variant: "destructive"
      });
    }
  };

  // Delete lead
  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead inquiry?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}?password=${adminPassword}`);
      toast({
        title: "Lead Deleted",
        description: "The inquiry has been permanently removed.",
        variant: "default"
      });
      refreshLeads();
    } catch (error) {
      toast({
        title: "Deletion Failed",
        description: "Could not delete lead inquiry.",
        variant: "destructive"
      });
    }
  };

  // AI Assistant message sending
  const handleChatSend = async (textToSend = null) => {
    const text = textToSend || chatInput;
    if (!text.trim()) return;

    if (!textToSend) setChatInput("");
    setChatLoading(true);

    // Optimistically add user message
    const userMsg = { role: "user", content: text };
    setChatMessages(prev => [...prev, userMsg]);

    try {
      // Setup SSE reader
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: chatSessionId, message: text })
      });

      if (!response.ok) throw new Error("Network response error");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let streamBuffer = "";
      setChatStreamingText("");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        streamBuffer += decoder.decode(value, { stream: true });
        const lines = streamBuffer.split("\n\n");
        // Keep the last incomplete block in the buffer
        streamBuffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.content) {
                setChatStreamingText(prev => prev + data.content);
              } else if (data.error) {
                toast({
                  title: "Chat Error",
                  description: data.error,
                  variant: "destructive"
                });
              }
            } catch (e) {
              // Parse error
            }
          }
        }
      }

      // Add final assistant message
      setChatMessages(prev => [...prev, { role: "assistant", content: chatStreamingText }]);
      setChatStreamingText("");
    } catch (error) {
      console.error("Chat streaming error:", error);
      setChatMessages(prev => [...prev, { role: "assistant", content: "I apologize, but I am experiencing connectivity issues. Please try calling us at 8108357916!" }]);
    } finally {
      setChatLoading(false);
      // Reload chat history to keep it synced
      loadChatHistory(chatSessionId);
    }
  };

  const quickQuestions = [
    "Tell me about the Makeup Course",
    "Where is the Academy?",
    "Do you offer placement support?",
    "Are fees affordable?"
  ];

  // Helper scroll function
  const scrollToSection = (sectionId) => {
    setActiveTab("home");
    setShowAdmin(false);
    setMobileMenuOpen(false);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  // Nav categories
  const navItems = [
    { name: "Home", id: "home-hero" },
    { name: "About Us", id: "about-us" },
    { name: "Courses", id: "courses" },
    { name: "Gallery", id: "gallery" },
    { name: "Student Reviews", id: "student-reviews" },
    { name: "Contact", id: "contact" }
  ];

  // Course Details
  const courses = [
    {
      title: "Professional Makeup",
      duration: "3 Months (Intensive)",
      level: "Beginner to Advanced",
      description: "Become a top-tier bridal, editorial, and HD makeup artist. Master shading, contouring, color theory, bridal glam, runway styling, and photographic corrections with premium global brands.",
      image: "https://images.unsplash.com/photo-1636023730877-233b9237d4ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtYWtldXAlMjBhcnRpc3QlMjB3b3JraW5nfGVufDB8fHx8MTt778&q=85&w=600",
      features: ["Bridal & HD Makeup", "Pro Kit Knowledge", "Photoshoot Portfolio", "Feminine Glamour Style"]
    },
    {
      title: "Advanced Hairstyling",
      duration: "2 Months",
      level: "Intermediate to Pro",
      description: "Design jaw-dropping bridal hairdos, editorial styles, extensions, and complex braiding techniques. Acquire critical knowledge of tools, hair styling products, blowouts, and hot styling treatments.",
      image: "https://images.unsplash.com/photo-1560869713-7d0a29430803?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxoYWlyJTIwc3R5bGluZyUyMHNhbG9uJTIwZWxlZ2FuY2V8ZW58MHx8fHwxN&q=85&w=600",
      features: ["Bridal Buns & Updos", "Modern Hair Braiding", "Hot Styling Tools Mastery", "Product Configuration"]
    },
    {
      title: "Professional Nail Art",
      duration: "1 Month",
      level: "Beginner to Pro",
      description: "Master modern nail enhancement. Learn precise acrylic extensions, luxury gel overlays, stunning chrome nails, gorgeous ombre gradients, custom hand-painted nail designs, and salon nail hygiene.",
      image: "https://images.pexels.com/photos/10194068/pexels-photo-10194068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=450&w=600",
      features: ["Acrylic & Gel Extensions", "3D & Chrome Nail Art", "Hygienic Manicure Prep", "Ombre Gradient Tech"]
    }
  ];

  // Gallery items
  const galleryItems = [
    {
      url: "https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/t0j921cd_IMG-20260703-WA0001.jpg",
      title: "Traditional Red Bridal",
      category: "Bridal Makeup"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/hy3ijnfz_IMG-20260703-WA0002.jpg",
      title: "Regal Kundan Glam",
      category: "Bridal Makeup"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/436fy19h_IMG-20260703-WA0003.jpg",
      title: "Christian Bridal Look",
      category: "Bridal Makeup"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/isafj0k6_IMG-20260703-WA0004.jpg",
      title: "Ethereal Engagement",
      category: "Bridal & Hair"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/jluv365k_IMG-20260703-WA0005.jpg",
      title: "Maharashtrian Nauvari",
      category: "Bridal Makeup"
    },
    {
      url: "https://images.unsplash.com/photo-1631549424057-403e75d68e2f?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
      title: "Floral Hairstyling",
      category: "Hairstyling"
    },
    {
      url: "https://images.unsplash.com/photo-1519014816548-bf5fe059798b?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
      title: "Signature Nail Artistry",
      category: "Nail Art"
    },
    {
      url: "https://images.unsplash.com/photo-1690749138086-7422f71dc159?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
      title: "Precision Manicure",
      category: "Nail Art"
    }
  ];

  // Reviews
  const reviews = [
    {
      name: "Pooja Sharma",
      role: "Certified Makeup Artist",
      rating: 5,
      text: "Enrolling at Sam Makeover Academy was the best decision of my life! The hands-on training with professional models gave me so much confidence. I launched my own bridal studio in Mumbai within 1 month of graduating!",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&q=80&w=150"
    },
    {
      name: "Anjali Gupta",
      role: "Professional Hairstylist",
      rating: 5,
      text: "The trainers are exceptionally patient and supportive. They don't just teach styling; they show you how to build a business. The modern, elegant studio environment in Goregaon West is truly inspiring.",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=80&w=150"
    },
    {
      name: "Sneha Patel",
      role: "Nail Artist & Entrepreneur",
      rating: 5,
      text: "Unbeatable placement assistance! I was placed with a premier celebrity salon right after completing my Professional Nail Art course. The fee is incredibly affordable for this level of luxury teaching.",
      avatar: "https://images.unsplash.com/photo-1508002366005-75a695ee2d17?crop=entropy&cs=srgb&fm=jpg&q=80&w=150"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col selection:bg-[#D4AF37] selection:text-black">
      
      {/* CRYSTAL GLASS NAVIGATION HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-[#D4AF37]/30 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div 
            onClick={() => scrollToSection("home-hero")} 
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/gl6dx80x_564605-removebg-preview.png" 
              alt="Sam Makeover Academy Logo" 
              className="w-12 h-12 object-contain group-hover:scale-110 transition-transform" 
            />
            <span className="font-display tracking-[0.15em] text-lg sm:text-2xl font-semibold uppercase text-white group-hover:text-[#D4AF37] transition-colors">
              Sam Makeover <span className="text-[#D4AF37]">Academy</span>
            </span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                data-testid={HOME[`nav${item.name.replace(/\s+/g, "")}`] || `nav-${item.name.toLowerCase()}`}
                onClick={() => {
                  setShowAdmin(false);
                  scrollToSection(item.id);
                }}
                className="text-sm tracking-[0.15em] uppercase text-gray-300 hover:text-[#D4AF37] transition-colors relative pb-1 group"
              >
                {item.name}
                <span className="absolute left-0 bottom-0 w-0 h-[1px] bg-[#D4AF37] transition-all group-hover:w-full" />
              </button>
            ))}
            
            <button
              data-testid={HOME.navAdmin}
              onClick={() => {
                setShowAdmin(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border rounded-none transition-all ${
                showAdmin 
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]" 
                  : "border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
              }`}
            >
              Admin Portal
            </button>
          </nav>

          {/* Mobile menu trigger */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white hover:text-[#D4AF37] transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu panel */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10 bg-black/95 overflow-hidden"
            >
              <div className="px-6 py-6 flex flex-col gap-5">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowAdmin(false);
                      scrollToSection(item.id);
                    }}
                    className="text-left text-sm tracking-[0.15em] uppercase text-gray-300 hover:text-[#D4AF37] py-2 border-b border-white/5"
                  >
                    {item.name}
                  </button>
                ))}
                <button
                  data-testid="mobile-nav-admin"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setShowAdmin(true);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="text-left text-sm tracking-[0.15em] uppercase text-[#D4AF37] py-2 border-b border-white/5"
                >
                  Admin Portal
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ADMIN PORTAL PANEL */}
      {showAdmin ? (
        <div data-testid={HOME.adminDashboard} className="flex-1 max-w-7xl mx-auto px-6 py-12 w-full">
          <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
            <div>
              <h1 className="text-3xl font-display font-bold tracking-tight text-[#D4AF37]">
                Academy Admissions Dashboard
              </h1>
              <p className="text-sm text-gray-400 mt-1 font-sans">
                Review, track, and manage direct course enrollment leads.
              </p>
            </div>
            {isAdminLoggedIn && (
              <button
                onClick={handleAdminLogout}
                className="flex items-center gap-2 text-xs tracking-wider uppercase border border-red-500/50 text-red-500 px-4 py-2 hover:bg-red-500 hover:text-white transition-all rounded-none"
              >
                <LogOut size={14} /> Logout
              </button>
            )}
          </div>

          {!isAdminLoggedIn ? (
            <div data-testid={HOME.adminLoginSection} className="max-w-md mx-auto bg-[#141414] border border-[#D4AF37]/30 p-8 shadow-2xl">
              <div className="flex flex-col items-center mb-6 text-center">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-3">
                  <Lock className="text-[#D4AF37] w-5 h-5" />
                </div>
                <h2 className="text-xl font-display font-semibold tracking-wide">Enter Administrator Password</h2>
                <p className="text-xs text-gray-400 mt-1">Authorized beauty academy personnel only.</p>
              </div>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Password</label>
                  <input
                    type="password"
                    data-testid={HOME.adminPasswordInput}
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-black border border-white/20 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  data-testid={HOME.adminLoginBtn}
                  disabled={loadingLeads}
                  className="w-full bg-[#D4AF37] text-black font-semibold uppercase tracking-widest text-xs py-3.5 hover:bg-[#F3E5AB] transition-colors rounded-none flex items-center justify-center gap-2"
                >
                  {loadingLeads ? <Loader2 className="animate-spin" size={16} /> : "Login and Synchronize"}
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Lead statistics cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { title: "Total Leads", count: leads.length, color: "text-[#D4AF37]" },
                  { title: "Pending", count: leads.filter(l => l.status === "Pending").length, color: "text-amber-500" },
                  { title: "In Contact", count: leads.filter(l => l.status === "In Contact").length, color: "text-blue-400" },
                  { title: "Enrolled", count: leads.filter(l => l.status === "Enrolled").length, color: "text-green-400" }
                ].map((stat, i) => (
                  <div key={i} className="bg-[#141414] border border-white/10 p-6 flex flex-col justify-between">
                    <span className="text-xs uppercase tracking-widest text-gray-400">{stat.title}</span>
                    <span className={`text-3xl font-bold font-display ${stat.color} mt-2`}>{stat.count}</span>
                  </div>
                ))}
              </div>

              {/* Actions panel */}
              <div className="flex justify-between items-center bg-[#141414] border border-white/10 p-4">
                <span className="text-sm font-sans text-gray-300">Synchronized directly with MongoDB collection <strong>leads</strong></span>
                <button
                  onClick={refreshLeads}
                  disabled={loadingLeads}
                  className="flex items-center gap-2 text-xs tracking-wider uppercase border border-white/20 px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all rounded-none"
                >
                  <RefreshCw className={loadingLeads ? "animate-spin" : ""} size={14} /> Refresh Leads
                </button>
              </div>

              {/* Leads Table */}
              <div className="bg-[#141414] border border-white/10 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-[#D4AF37] bg-black/40">
                      <th className="p-4 font-semibold">Student Name</th>
                      <th className="p-4 font-semibold">Inquired Course</th>
                      <th className="p-4 font-semibold">Contact Info</th>
                      <th className="p-4 font-semibold">Message</th>
                      <th className="p-4 font-semibold">Inquiry Date</th>
                      <th className="p-4 font-semibold">Status</th>
                      <th className="p-4 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-500 text-sm font-sans">
                          No enrollment leads found in the database.
                        </td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr 
                          key={lead.id} 
                          data-testid={HOME.leadRow}
                          className="border-b border-white/5 hover:bg-white/5 transition-colors font-sans text-sm"
                        >
                          <td className="p-4 font-medium text-white font-display text-base">{lead.name}</td>
                          <td className="p-4">
                            <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-2.5 py-1 text-xs uppercase tracking-wider font-semibold border border-[#D4AF37]/20">
                              {lead.course}
                            </span>
                          </td>
                          <td className="p-4 space-y-1 text-gray-300">
                            <div>{lead.email}</div>
                            <div className="text-xs text-gray-400 font-mono">{lead.phone}</div>
                          </td>
                          <td className="p-4 text-xs max-w-xs truncate text-gray-400" title={lead.message}>
                            {lead.message || "—"}
                          </td>
                          <td className="p-4 text-xs text-gray-400">
                            {new Date(lead.created_at).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric"
                            })}
                          </td>
                          <td className="p-4">
                            <select
                              data-testid={HOME.leadStatusSelect}
                              value={lead.status}
                              onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                              className={`bg-black border text-xs tracking-wider uppercase font-semibold py-1 px-2 focus:outline-none focus:border-[#D4AF37] ${
                                lead.status === "Enrolled" ? "text-green-400 border-green-500/50" :
                                lead.status === "In Contact" ? "text-blue-400 border-blue-500/50" :
                                lead.status === "Cancelled" ? "text-red-400 border-red-500/50" :
                                "text-amber-500 border-amber-500/50"
                              }`}
                            >
                              <option value="Pending" className="bg-[#141414]">Pending</option>
                              <option value="In Contact" className="bg-[#141414]">In Contact</option>
                              <option value="Enrolled" className="bg-[#141414]">Enrolled</option>
                              <option value="Cancelled" className="bg-[#141414]">Cancelled</option>
                            </select>
                          </td>
                          <td className="p-4 text-right">
                            <button
                              data-testid={HOME.leadDeleteBtn}
                              onClick={() => deleteLead(lead.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              title="Delete Lead"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Gallery Management */}
              <div className="bg-[#141414] border border-[#D4AF37]/20 p-6 space-y-6" data-testid="admin-gallery-manager">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="text-lg font-display font-semibold text-white">Gallery & Media Manager</h3>
                    <p className="text-xs text-gray-400 font-sans mt-1">Upload your work photos — they appear instantly in the public gallery.</p>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-[#D4AF37]">{galleryUploads.length} Uploaded</span>
                </div>

                {/* Upload Form */}
                <form onSubmit={handleGalleryUpload} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-b border-white/10 pb-6">
                  <div className="md:col-span-1">
                    <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2">Image File *</label>
                    <input
                      id="gallery-file-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      data-testid="gallery-file-input"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="w-full text-xs text-gray-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#D4AF37] file:text-black file:text-xs file:font-semibold file:uppercase file:tracking-wider file:cursor-pointer bg-black border border-white/20 py-1.5 px-2"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2">Title</label>
                    <input
                      type="text"
                      value={uploadTitle}
                      data-testid="gallery-title-input"
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder="e.g. Royal Bridal Look"
                      className="w-full bg-black border border-white/20 text-white px-3 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none rounded-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-2">Category</label>
                    <select
                      value={uploadCategory}
                      data-testid="gallery-category-select"
                      onChange={(e) => setUploadCategory(e.target.value)}
                      className="w-full bg-black border border-white/20 text-white px-3 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none rounded-none appearance-none"
                    >
                      <option value="Bridal Makeup">Bridal Makeup</option>
                      <option value="Party Makeup">Party Makeup</option>
                      <option value="Hairstyling">Hairstyling</option>
                      <option value="Nail Art">Nail Art</option>
                      <option value="Gallery">Other</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={uploading}
                    data-testid="gallery-upload-btn"
                    className="bg-[#D4AF37] text-black font-semibold uppercase tracking-widest text-xs py-3 hover:bg-[#F3E5AB] transition-colors rounded-none flex items-center justify-center gap-2"
                  >
                    {uploading ? <Loader2 className="animate-spin" size={16} /> : "Upload Image"}
                  </button>
                </form>

                {/* Uploaded Images Grid */}
                {galleryUploads.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm py-6 font-sans">No images uploaded yet. Add your first work photo above.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {galleryUploads.map((img) => (
                      <div key={img.id} className="relative group border border-white/10 overflow-hidden" data-testid="admin-gallery-item">
                        <div className="aspect-square overflow-hidden">
                          <img src={`${API}/files/${img.storage_path}`} alt={img.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2 text-center">
                          <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{img.category}</span>
                          <span className="text-xs text-white font-display line-clamp-2">{img.title}</span>
                          <button
                            onClick={() => deleteGalleryImage(img.id)}
                            data-testid="gallery-delete-btn"
                            className="mt-1 text-red-400 hover:text-red-500 flex items-center gap-1 text-[11px] uppercase tracking-wider"
                          >
                            <Trash2 size={13} /> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* CORE MARKETING EXPERIENCE */
        <main className="flex-grow">
          
          {/* LARGE HERO BANNER */}
          <section 
            id="home-hero" 
            className="relative min-h-[90vh] flex items-center justify-center bg-cover bg-center overflow-hidden py-24 px-6"
            style={{ 
              backgroundImage: `linear-gradient(to bottom, rgba(10,10,10,0.5), rgba(10,10,10,0.85)), url("https://images.pexels.com/photos/13336910/pexels-photo-13336910.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")` 
            }}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
            
            <div className="relative max-w-5xl mx-auto text-center z-10 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                className="space-y-4 flex flex-col items-center"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/gl6dx80x_564605-removebg-preview.png" 
                  alt="Sam Makeover Academy Emblem" 
                  className="w-48 sm:w-60 h-auto object-contain mb-6 drop-shadow-2xl" 
                />
                <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#D4AF37]/30 bg-black/40 backdrop-blur-md rounded-none mb-4">
                  <Award className="text-[#D4AF37] w-4 h-4 stroke-[1px]" />
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Premium Beauty Academy</span>
                </div>
                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-display font-extrabold tracking-tight text-white leading-[1.1]">
                  Become a Certified <br className="hidden sm:inline" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] via-[#FFF] to-[#D4AF37] bg-size-200">
                    Professional Makeup Artist
                  </span>
                </h1>
                <p className="text-base sm:text-xl text-gray-300 font-sans max-w-3xl mx-auto font-light leading-relaxed">
                  Professional Makeup, Hairstyling & Nail Art Courses in Goregaon West, Mumbai. Learn from master trainers and kickstart your dream beauty career.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
              >
                <button
                  data-testid={HOME.heroEnrollBtn}
                  onClick={() => setShowEnrollModal(true)}
                  className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#F3E5AB] text-black font-semibold text-xs uppercase tracking-[0.2em] px-8 py-4 transition-colors duration-300 rounded-none shadow-xl border border-[#D4AF37] flex items-center justify-center gap-2"
                >
                  <Sparkles size={16} /> Enroll Now
                </button>
                <a
                  href="tel:8108357916"
                  data-testid={HOME.heroCallBtn}
                  className="w-full sm:w-auto bg-transparent hover:bg-white/10 text-[#D4AF37] hover:text-[#D4AF37] font-semibold text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#D4AF37] transition-all duration-300 rounded-none flex items-center justify-center gap-2"
                >
                  <Phone size={16} /> Call Now
                </a>
              </motion.div>

              {/* Scroll down mouse indicator */}
              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="pt-12 hidden sm:flex flex-col items-center gap-2 text-gray-400 cursor-pointer"
                onClick={() => scrollToSection("why-choose-us")}
              >
                <span className="text-[10px] tracking-[0.2em] uppercase">Scroll to Explore</span>
                <div className="w-1.5 h-6 bg-white/20 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-[#D4AF37] rounded-full mt-1" />
                </div>
              </motion.div>
            </div>
          </section>

          {/* WHY CHOOSE US SECTION */}
          <section id="why-choose-us" className="bg-[#0D0D0D] py-24 px-6 border-y border-white/5 relative">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                
                {/* Left: Beautiful overlapping image grids */}
                <div className="lg:col-span-5 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent blur-3xl rounded-full" />
                  <div className="relative border border-[#D4AF37]/20 p-3 bg-black">
                    <img 
                      src="https://images.pexels.com/photos/6953622/pexels-photo-6953622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                      alt="Sam Academy Hands-on Practice"
                      className="w-full max-w-sm aspect-[4/5] object-cover filter brightness-90 grayscale-[20%] hover:scale-[1.03] transition-transform duration-500"
                    />
                    <div className="absolute -bottom-8 -right-8 bg-[#141414] border border-[#D4AF37]/30 p-6 hidden sm:block max-w-xs">
                      <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Mumbai&apos;s Premier Academy</p>
                      <p className="text-lg font-display text-white mt-1 font-semibold">100% Practical Training</p>
                    </div>
                  </div>
                </div>

                {/* Right: Editorial Why Choose Us points */}
                <div className="lg:col-span-7 space-y-12">
                  <div>
                    <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Your Future Starts Here</span>
                    <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white mt-3 leading-tight">
                      Why Choose <br className="hidden sm:inline" />
                      Sam Makeover Academy?
                    </h2>
                    <p className="text-sm sm:text-base text-gray-400 mt-4 leading-relaxed max-w-2xl font-sans">
                      Our signature educational style blends intensive hands-on practical artistry with modern salon business acumen. We prepare you to step directly into the multi-billion rupee premium beauty industry as a highly respected certified artist.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {[
                      {
                        title: "Professional Trainers",
                        desc: "Learn closely from industry-celebrated master makeup artists and styling consultants."
                      },
                      {
                        title: "Hands-on Practice",
                        desc: "Get extensive everyday practical session exposure on multiple skin types and hair textures."
                      },
                      {
                        title: "Certification",
                        desc: "Earn widely recognized credentials that command premium client bookings instantly."
                      },
                      {
                        title: "Placement Assistance",
                        desc: "Gain secure entry into premier salons, bridal boutiques, fashion houses, and media productions."
                      },
                      {
                        title: "Affordable Fees",
                        desc: "Pay reasonable course rates comfortably with flexible installment plans and zero hidden costs."
                      }
                    ].map((point, index) => (
                      <div key={index} className="flex gap-4 group">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20 group-hover:bg-[#D4AF37] transition-all duration-300">
                          <CheckCircle size={16} className="text-[#D4AF37] group-hover:text-black transition-colors" />
                        </div>
                        <div>
                          <h3 className="text-lg font-display font-semibold text-white group-hover:text-[#D4AF37] transition-colors">{point.title}</h3>
                          <p className="text-xs sm:text-sm text-gray-400 mt-1 font-sans leading-relaxed">{point.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* ABOUT US SECTION */}
          <section id="about-us" className="bg-[#0A0A0A] py-24 px-6 relative overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                
                <div className="lg:col-span-7 space-y-6 order-2 lg:order-1">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">About Sam Makeover Academy</span>
                  <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white leading-tight">
                    Shaping the Future of Beauty Elite
                  </h2>
                  <p className="text-sm sm:text-base text-gray-300 font-sans leading-relaxed">
                    Sam Makeover Academy is Goregaon West&apos;s leading certified beauty academy dedicated to elevating makeup, hairstyling, and nail artistry. Led by visionary artist Sam, our modern academy acts as a launchpad for future elite beauty entrepreneurs, stylists, and bridal experts.
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 font-sans leading-relaxed">
                    Located strategically near Goregaon Railway Station in Mumbai, our state-of-the-art beauty academy replicates high-end premium salon environments. We focus heavily on everyday individual supervision, premium tool mastery, and high-impact industry placement, ensuring zero compromises in your professional beauty education.
                  </p>
                  <div className="pt-4 flex flex-wrap gap-8 items-center border-t border-white/5">
                    <div>
                      <h4 className="text-3xl font-display font-bold text-[#D4AF37]">500+</h4>
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mt-1">Certified Students</p>
                    </div>
                    <div>
                      <h4 className="text-3xl font-display font-bold text-[#D4AF37]">100%</h4>
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mt-1">Practical Classes</p>
                    </div>
                    <div>
                      <h4 className="text-3xl font-display font-bold text-[#D4AF37]">8+ Years</h4>
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mt-1">Artistry Experience</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-5 relative order-1 lg:order-2 flex items-center justify-center">
                  <div className="absolute inset-0 bg-[#D4AF37]/5 blur-2xl" />
                  <div className="relative border border-[#D4AF37]/20 p-3 bg-black">
                    <img 
                      src="https://images.unsplash.com/photo-1508002366005-75a695ee2d17?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=600"
                      alt="About Sam Academy"
                      className="w-full max-w-sm aspect-[4/5] object-cover brightness-95 grayscale-[10%] hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                </div>

              </div>

              {/* Elegant Gold Divider */}
              <div className="border-t border-[#D4AF37]/20 my-20" />

              {/* MEET OUR FOUNDER SECTION */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                
                <div className="lg:col-span-5 relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-[#D4AF37]/5 blur-2xl" />
                  <div className="relative border border-[#D4AF37]/20 p-3 bg-black">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/lkc98d9l_file_0000000048e072079f831dd63625f132.png"
                      alt="Ms. Sampada Rane - Founder"
                      className="w-full max-w-sm aspect-[4/5] object-cover brightness-95 grayscale-[10%] hover:scale-[1.02] transition-transform duration-500"
                    />
                    <div className="absolute -bottom-6 -left-6 bg-[#141414] border border-[#D4AF37]/30 px-5 py-3 hidden sm:block">
                      <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold">Founder & Director</p>
                      <p className="text-sm font-display text-white font-semibold mt-0.5">Ms. Sampada Rane</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Meet Our Founder</span>
                  <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-white leading-tight">
                    Ms. Sampada Rane
                  </h2>
                  <p className="text-sm sm:text-base text-gray-300 font-sans leading-relaxed">
                    Ms. Sampada Rane is the passionate founder of Sam Makeover Academy and a dedicated beauty educator with over 5 years of professional experience in the beauty and makeup industry. Her vision is to empower aspiring artists by providing practical, industry-focused training that builds both confidence and creativity.
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 font-sans leading-relaxed">
                    With a commitment to excellence and personalized mentorship, Ms. Rane has successfully trained more than 300+ students, many of whom have gone on to build successful careers as professional makeup artists, hairstylists, nail technicians, and beauty entrepreneurs.
                  </p>
                  <p className="text-sm sm:text-base text-gray-400 font-sans leading-relaxed">
                    At Sam Makeover Academy, she believes that every student has unique potential. Through hands-on training, expert guidance, and continuous support, her mission is to help every learner transform their passion into a rewarding profession.
                  </p>
                  
                  <div className="border-l-2 border-[#D4AF37] pl-4 mt-6">
                    <p className="italic font-display text-base sm:text-lg text-[#D4AF37] leading-relaxed">
                      &ldquo;Beauty is an art, and every artist deserves the right guidance to shine.&rdquo;
                    </p>
                  </div>
                </div>

              </div>

            </div>
          </section>

          {/* COURSE SECTION */}
          <section id="courses" className="bg-[#0D0D0D] py-24 px-6 border-y border-white/5 relative">
            <div className="max-w-7xl mx-auto">
              <div className="text-center space-y-4 mb-16">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Exquisite Programs</span>
                <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white">Our Signature Courses</h2>
                <p className="text-sm text-gray-400 max-w-2xl mx-auto font-sans">
                  Choose from our carefully curated professional certifications. Each course is packed with structured daily lessons, master trainer hands-on practice, and premium student kits.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {courses.map((course, index) => (
                  <div 
                    key={index} 
                    className="bg-[#141414] border border-[#D4AF37]/20 flex flex-col justify-between hover:-translate-y-2 transition-all duration-300 group"
                  >
                    <div>
                      {/* Course Image */}
                      <div className="overflow-hidden aspect-video relative">
                        <img 
                          src={course.image} 
                          alt={course.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 brightness-90 filter group-hover:brightness-100"
                        />
                        <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold border border-[#D4AF37]/30">
                          {course.duration}
                        </div>
                      </div>

                      {/* Course Details */}
                      <div className="p-8 space-y-4">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold">{course.level}</span>
                        <h3 className="text-2xl font-display font-semibold text-white group-hover:text-[#D4AF37] transition-colors">{course.title}</h3>
                        <p className="text-xs sm:text-sm text-gray-400 font-sans leading-relaxed">
                          {course.description}
                        </p>
                        
                        <div className="pt-4 border-t border-white/5 space-y-2">
                          <p className="text-[11px] uppercase tracking-wider text-[#D4AF37] font-semibold">What you&apos;ll master:</p>
                          <div className="grid grid-cols-1 gap-1.5">
                            {course.features.map((feat, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-gray-300 font-sans">
                                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-none flex-shrink-0" />
                                <span>{feat}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Actions */}
                    <div className="p-8 pt-0">
                      <button
                        data-testid={`enroll-course-btn-${course.title.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => {
                          setEnrollForm(prev => ({ ...prev, course: course.title }));
                          setShowEnrollModal(true);
                        }}
                        className="w-full bg-transparent group-hover:bg-[#D4AF37] text-[#D4AF37] group-hover:text-black font-semibold text-xs uppercase tracking-[0.2em] py-3.5 border border-[#D4AF37]/70 group-hover:border-[#D4AF37] transition-all duration-300 rounded-none flex items-center justify-center gap-2"
                      >
                        Enroll Now <ChevronRight size={14} />
                      </button>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* GALLERY SECTION */}
          <section id="gallery" className="bg-[#0A0A0A] py-24 px-6 relative overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-4">
                <div>
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Sam Makeover Gallery</span>
                  <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white mt-3">Our Masterpieces</h2>
                  <p className="text-sm text-gray-400 mt-2 font-sans max-w-xl">
                    View authentic student portfolio projects and premium bridal assignments shot inside our high-end Mumbai makeup studio.
                  </p>
                </div>
                <a
                  href="https://instagram.com/sampadaa_makeover"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={HOME.instagramBtn}
                  className="flex items-center gap-2 text-xs tracking-widest uppercase border border-white/20 hover:border-[#D4AF37] text-white hover:text-[#D4AF37] px-5 py-3 transition-colors rounded-none"
                >
                  <Instagram size={14} /> View Instagram Feed
                </a>
              </div>

              {/* Tetris Asymmetric Grid Gallery */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  ...galleryUploads.map((u) => ({ url: `${API}/files/${u.storage_path}`, title: u.title, category: u.category })),
                  ...galleryItems
                ].map((item, index) => (
                  <div 
                    key={index} 
                    className="relative overflow-hidden group border border-white/10"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img 
                        src={item.url} 
                        alt={item.title}
                        className="w-full h-full object-cover filter brightness-90 group-hover:scale-110 group-hover:brightness-100 transition-all duration-700"
                      />
                    </div>
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-6">
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-semibold">{item.category}</span>
                      <h3 className="text-lg font-display font-semibold text-white mt-1">{item.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* STUDENT TESTIMONIALS SECTION */}
          <section id="student-reviews" className="bg-[#0D0D0D] py-24 px-6 border-y border-white/5 relative">
            <div className="max-w-7xl mx-auto">
              <div className="text-center space-y-4 mb-16">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Alumni Voices</span>
                <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white">Student Reviews</h2>
                <p className="text-sm text-gray-400 max-w-2xl mx-auto font-sans">
                  Read real stories of transformation from our hard-working graduates who have opened successful salons and launched elite freelance careers across India.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {reviews.map((rev, index) => (
                  <div key={index} className="bg-[#141414] border border-white/10 p-8 flex flex-col justify-between relative group">
                    <span className="text-5xl font-display text-[#D4AF37]/20 absolute top-4 left-6 group-hover:text-[#D4AF37]/30 transition-colors">“</span>
                    
                    <div className="space-y-4 relative z-10">
                      <div className="flex gap-1">
                        {[...Array(rev.rating)].map((_, i) => (
                          <Star key={i} size={14} className="text-[#D4AF37] fill-[#D4AF37]" />
                        ))}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-300 font-sans leading-relaxed italic font-light">
                        &ldquo;{rev.text}&rdquo;
                      </p>
                    </div>

                    <div className="flex items-center gap-4 pt-6 border-t border-white/5 mt-6 relative z-10">
                      <img 
                        src={rev.avatar} 
                        alt={rev.name}
                        className="w-10 h-10 rounded-none object-cover border border-[#D4AF37]/40"
                      />
                      <div>
                        <h4 className="text-sm font-display font-semibold text-white">{rev.name}</h4>
                        <p className="text-[11px] text-gray-400 font-sans mt-0.5">{rev.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CONTACT SECTION WITH GOOGLE MAP */}
          <section id="contact" className="bg-[#0A0A0A] py-24 px-6 relative">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                
                {/* Contact Information & Map */}
                <div className="lg:col-span-5 space-y-8">
                  <div>
                    <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Join the Academy</span>
                    <h2 className="text-3xl sm:text-5xl font-display tracking-tight text-white mt-3">Contact Us</h2>
                    <p className="text-sm text-gray-400 mt-2 font-sans leading-relaxed">
                      Visit our Goregaon West studio. Talk to our master trainers, check out the classes live, and select your customized career path.
                    </p>
                  </div>

                  <div className="space-y-4 font-sans text-sm">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                        <MapPin size={16} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Academy Address</h4>
                        <p className="text-gray-300 mt-1 leading-relaxed">
                          Topiwala Mall Shop No.30,<br />
                          Near Goregaon Railway Station,<br />
                          Goregaon West, Mumbai.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                        <Phone size={16} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Phone & WhatsApp</h4>
                        <p className="text-gray-300 mt-1 font-mono hover:text-[#D4AF37] transition-colors">
                          <a href="tel:8108357916">8108357916</a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Google Map Embed */}
                  <div className="border border-[#D4AF37]/20 relative overflow-hidden bg-[#141414] aspect-video w-full">
                    <iframe
                      title="Sam Makeover Academy Location"
                      src="https://www.google.com/maps?q=Topiwala+Mall+Goregaon+West+Mumbai&output=embed"
                      className="w-full h-full grayscale-[0.3] contrast-125"
                      style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      data-testid="contact-google-map"
                    />
                  </div>
                </div>

                {/* Lead Submission Form */}
                <div className="lg:col-span-7 bg-[#141414] border border-[#D4AF37]/20 p-8 sm:p-10 shadow-2xl relative">
                  <div className="absolute top-0 right-0 border-t-[12px] border-r-[12px] border-t-[#D4AF37] border-r-[#D4AF37] border-l-[12px] border-l-transparent border-b-[12px] border-b-transparent" />
                  
                  <h3 className="text-2xl font-display font-semibold tracking-wide text-white mb-2">Book Your Free Counseling Session</h3>
                  <p className="text-xs text-gray-400 font-sans mb-8">Fill in your information and master trainers will contact you shortly.</p>
                  
                  <form onSubmit={handleEnrollSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Your Name *</label>
                        <input
                          type="text"
                          data-testid={HOME.formNameInput}
                          value={enrollForm.name}
                          onChange={(e) => setEnrollForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Pooja Sharma"
                          className="w-full bg-black/40 border border-white/20 text-white px-4 py-3.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Email Address *</label>
                        <input
                          type="email"
                          data-testid={HOME.formEmailInput}
                          value={enrollForm.email}
                          onChange={(e) => setEnrollForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="pooja@gmail.com"
                          className="w-full bg-black/40 border border-white/20 text-white px-4 py-3.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          data-testid={HOME.formPhoneInput}
                          value={enrollForm.phone}
                          onChange={(e) => setEnrollForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="8108357916"
                          className="w-full bg-black/40 border border-white/20 text-white px-4 py-3.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Select Course *</label>
                        <select
                          data-testid={HOME.formCourseSelect}
                          value={enrollForm.course}
                          onChange={(e) => setEnrollForm(prev => ({ ...prev, course: e.target.value }))}
                          className="w-full bg-black/40 border border-white/20 text-white px-4 py-3.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans appearance-none"
                          required
                        >
                          <option value="Professional Makeup" className="bg-[#141414]">Professional Makeup</option>
                          <option value="Advanced Hairstyling" className="bg-[#141414]">Advanced Hairstyling</option>
                          <option value="Professional Nail Art" className="bg-[#141414]">Professional Nail Art</option>
                          <option value="Other / Combo Course" className="bg-[#141414]">Other / Combo Course</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-400 mb-2">Message or Inquiry Notes</label>
                      <textarea
                        data-testid={HOME.formMessageTextarea}
                        value={enrollForm.message}
                        onChange={(e) => setEnrollForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="I want to know about weekend batches, installment payment schedules, and what items are included inside the free makeup kit..."
                        rows="4"
                        className="w-full bg-black/40 border border-white/20 text-white px-4 py-3.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid={HOME.formSubmitBtn}
                      disabled={formSubmitting}
                      className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-black font-semibold uppercase tracking-widest text-xs py-4 transition-colors duration-300 rounded-none flex items-center justify-center gap-2"
                    >
                      {formSubmitting ? <Loader2 className="animate-spin" size={16} /> : "Submit Inquiry"}
                    </button>
                  </form>
                </div>

              </div>
            </div>
          </section>

        </main>
      )}

      {/* FOOTER */}
      <footer className="bg-black border-t border-[#D4AF37]/20 py-16 px-6 relative mt-auto">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 text-sm font-sans">
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_sam-makeover/artifacts/gl6dx80x_564605-removebg-preview.png" 
                alt="Sam Makeover Academy" 
                className="w-10 h-10 object-contain" 
              />
              <span className="font-display tracking-[0.15em] text-lg font-bold uppercase text-white">
                Sam Makeover
              </span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed font-light">
              Premium Beauty Academy shaping top-tier professional makeup artists, hairstylists, and nail techs in Goregaon West, Mumbai.
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="font-display text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Quick Links</h4>
            <div className="flex flex-col gap-2.5">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-xs text-gray-400 hover:text-[#D4AF37] transition-colors w-max"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-display text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Contact Details</h4>
            <div className="text-xs text-gray-400 space-y-2 font-light">
              <p>Topiwala Mall Shop No.30, Near Goregaon Railway Station, Goregaon West, Mumbai.</p>
              <p className="font-mono pt-1">Phone: 8108357916</p>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-display text-xs uppercase tracking-widest text-[#D4AF37] font-semibold">Connect with us</h4>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/sampadaa_makeover" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-9 h-9 border border-white/20 hover:border-[#D4AF37] flex items-center justify-center text-gray-300 hover:text-[#D4AF37] transition-colors"
                title="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a 
                href="https://wa.me/918108357916" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-9 h-9 border border-white/20 hover:border-[#D4AF37] flex items-center justify-center text-gray-300 hover:text-[#D4AF37] transition-colors"
                title="WhatsApp"
              >
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

        </div>

        <div className="max-w-7xl mx-auto border-t border-white/5 mt-12 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500 font-light font-sans">
          <span>&copy; {new Date().getFullYear()} Sam Makeover Academy. All Rights Reserved.</span>
          <span className="flex items-center gap-1">
            Made with luxury and precision by Emergent Labs.
          </span>
        </div>
      </footer>

      {/* FLOATING ACTION: FIXED GREEN/GOLD WHATSAPP PULSING BUTTON */}
      <a
        href="https://wa.me/918108357916"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={HOME.whatsappFab}
        className="fixed bottom-6 right-6 z-40 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
        title="Chat on WhatsApp"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping group-hover:hidden" />
        <MessageCircle size={24} className="stroke-[1.5px]" />
      </a>

      {/* FLOATING ACTION: AI BEAUTY ADVISOR CHATBOT */}
      <div className="fixed bottom-24 right-6 z-40 flex flex-col items-end">
        <AnimatePresence>
          {chatOpen && (
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              data-testid={HOME.chatWindow}
              className="w-80 sm:w-96 h-[500px] bg-[#141414] border border-[#D4AF37]/40 shadow-2xl flex flex-col overflow-hidden mb-3"
            >
              {/* Chat Header */}
              <div className="bg-black p-4 flex justify-between items-center border-b border-[#D4AF37]/25">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/50">
                      <Sparkles size={14} className="text-[#D4AF37]" />
                    </div>
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-[#141414] rounded-full" />
                  </div>
                  <div>
                    <h4 className="text-sm font-display font-bold text-white uppercase tracking-wider">Sam Course Advisor</h4>
                    <span className="text-[10px] text-green-400 font-sans font-light">Online • Powered by AI</span>
                  </div>
                </div>
                <button 
                  onClick={() => setChatOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-black/40 font-sans text-xs">
                {chatMessages.length === 0 && (
                  <div className="text-center py-8 px-4 space-y-3">
                    <Sparkles className="text-[#D4AF37] w-6 h-6 mx-auto stroke-[1px] animate-pulse" />
                    <p className="text-gray-300 font-medium font-display text-sm uppercase tracking-wider">Welcome to Sam Academy!</p>
                    <p className="text-gray-400 leading-relaxed max-w-xs mx-auto">
                      Hello! I am Sam, your personal luxury AI advisor. Ask me anything about our courses, timings, placement, or Goregaon West studio location.
                    </p>
                  </div>
                )}
                
                {chatMessages.map((msg, i) => (
                  <div 
                    key={i} 
                    data-testid={`${HOME.chatMessage}-${i}`}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] px-4 py-3 rounded-none border ${
                      msg.role === 'user' 
                        ? 'bg-[#D4AF37]/10 text-white border-[#D4AF37]/40' 
                        : 'bg-[#141414] text-gray-100 border-white/5'
                    }`}>
                      <p className="leading-relaxed whitespace-pre-line">{msg.content}</p>
                    </div>
                  </div>
                ))}

                {/* Streaming Response */}
                {chatStreamingText && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] px-4 py-3 rounded-none border bg-[#141414] text-gray-100 border-white/5">
                      <p className="leading-relaxed whitespace-pre-line">{chatStreamingText}</p>
                    </div>
                  </div>
                )}

                {/* Loading indicator */}
                {chatLoading && !chatStreamingText && (
                  <div className="flex justify-start">
                    <div className="bg-[#141414] border border-white/5 p-4 rounded-none flex items-center gap-2">
                      <Loader2 className="animate-spin text-[#D4AF37]" size={14} />
                      <span className="text-gray-400">Sam is crafting advice...</span>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Quick Question Chips */}
              <div className="px-4 py-2 border-t border-white/5 bg-black/40 flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-none">
                {quickQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleChatSend(q)}
                    disabled={chatLoading}
                    className="text-[10px] bg-white/5 border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-gray-300 px-3 py-1.5 transition-all rounded-none font-sans disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>

              {/* Chat Input */}
              <form 
                onSubmit={(e) => { e.preventDefault(); handleChatSend(); }}
                className="p-3 bg-black border-t border-[#D4AF37]/20 flex gap-2"
              >
                <input
                  type="text"
                  data-testid={HOME.chatInput}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Ask about timings, certificate, pricing..."
                  className="flex-grow bg-[#141414] border border-white/15 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                  disabled={chatLoading}
                  required
                />
                <button
                  type="submit"
                  data-testid={HOME.chatSendBtn}
                  disabled={chatLoading || !chatInput.trim()}
                  className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] p-2.5 transition-colors rounded-none disabled:opacity-50 flex items-center justify-center"
                >
                  <Send size={14} />
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Toggle Bubble */}
        <button
          onClick={() => setChatOpen(!chatOpen)}
          data-testid={HOME.chatToggleBtn}
          className="bg-black border border-[#D4AF37]/80 hover:border-[#D4AF37] hover:bg-[#141414] text-[#D4AF37] p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
          title="AI Course Recommendation"
        >
          {chatOpen ? <X size={20} /> : <Sparkles size={20} className="animate-pulse" />}
        </button>
      </div>

      {/* INTERACTIVE ENROLLMENT OVERLAY MODAL */}
      <AnimatePresence>
        {showEnrollModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div 
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-[#141414] border border-[#D4AF37]/40 max-w-lg w-full p-8 relative shadow-2xl overflow-hidden"
            >
              <button 
                onClick={() => setShowEnrollModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                title="Close"
              >
                <X size={20} />
              </button>
              
              <div className="mb-6 space-y-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Sam Makeover Academy</span>
                <h3 className="text-2xl font-display font-semibold text-white">Enrollment Inquiry</h3>
                <p className="text-xs text-gray-400 font-sans">Fill in the fields below and we&apos;ll book your seat and demo class.</p>
              </div>

              <form onSubmit={handleEnrollSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={enrollForm.name}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Pooja Sharma"
                    className="w-full bg-black border border-white/20 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={enrollForm.email}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="pooja@gmail.com"
                    className="w-full bg-black border border-white/20 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    value={enrollForm.phone}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="8108357916"
                    className="w-full bg-black border border-white/20 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Preferred Program *</label>
                  <select
                    value={enrollForm.course}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, course: e.target.value }))}
                    className="w-full bg-black border border-white/20 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans appearance-none"
                    required
                  >
                    <option value="Professional Makeup">Professional Makeup</option>
                    <option value="Advanced Hairstyling">Advanced Hairstyling</option>
                    <option value="Professional Nail Art">Professional Nail Art</option>
                    <option value="Other / Combo Course">Other / Combo Course</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-gray-400 mb-1">Inquiry Message</label>
                  <textarea
                    value={enrollForm.message}
                    onChange={(e) => setEnrollForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Provide batch preference or any specific queries..."
                    rows="3"
                    className="w-full bg-black border border-white/20 text-white px-4 py-2.5 text-xs focus:border-[#D4AF37] focus:outline-none transition-colors rounded-none font-sans resize-none"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-black font-semibold uppercase tracking-widest text-xs py-3.5 transition-colors duration-300 rounded-none flex items-center justify-center gap-2"
                  >
                    {formSubmitting ? <Loader2 className="animate-spin" size={16} /> : "Submit Secure Enrollment"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Toaster />
    </div>
  );
}
